/**
 * Frontend auth helpers for BFF pattern.
 *
 * Backend handles all OAuth — browser holds NO tokens. Session lives in an
 * HttpOnly cookie that the browser auto-attaches via `credentials: include`.
 *
 * - `login(returnTo)` — kick off backend-driven OAuth flow
 * - `logout()` — kill backend session, redirect home
 * - `getMe()` — fetch current user from backend (or null on 401)
 */

const API_BASE = ((import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ?? "").replace(/\/$/, "");

export interface User {
	userId: string;
	role?: string;
	sessionCreatedAt: string;
}

export function login(returnTo: string = window.location.pathname + window.location.search): never {
	const params = new URLSearchParams({ return: returnTo });
	window.location.href = `${API_BASE}/api/auth/login?${params}`;
	throw new Error("redirecting"); // unreachable
}

export function logout(): never {
	// Single navigation to the backend's GET /logout — it atomically clears
	// the session cookie AND 302s to auth.g1.network/oauth/end_session, which
	// in turn clears the .g1.network SSO cookies and redirects back to us.
	//
	// The previous fetch(POST) + window.location combo had a timing race: the
	// browser sometimes hadn't applied the Set-Cookie deletion by the time the
	// navigation fired, so the local session survived end_session's bounce.
	window.location.href = `${API_BASE}/api/auth/logout`;
	throw new Error("redirecting"); // unreachable
}

export async function getMe(): Promise<User | null> {
	const res = await fetch(`${API_BASE}/api/auth/me`, { credentials: "include" });
	if (res.status === 401) return null;
	if (!res.ok) throw new Error(`/api/auth/me → ${res.status}`);
	return (await res.json()) as User;
}
